// React
import { useState, useEffect, useContext } from 'react';
// Components
import { Typography, FormControl, Button, Dialog, DialogContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { AuthContext } from 'context/auth-context';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import CircularComponent from 'components/shared/CircularComponent';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
// Assets
import { ReactComponent as InfoIcon } from 'assets/modalKYX/InfoIconBlack.svg';
// Libs/APIs
import { KYB } from 'lib/api';
// Helpers
import { OWNERSHIP_DISCLOSURE_STATUS } from 'helpers/constants';

const useStyles = makeStyles(theme => ({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '500px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important',
        color: '#182C55'
    },
    phoneBox: {
        marginTop: '2em'
    },
    phoneInput: {
        width: '50%'
    },
    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important',
        background: '#1BABE2!important'
    },
    inputFormControl: {
        background: '#FFFFFF',
        border: '1px solid #D8DDE2',
        boxSizing: 'border-box',
        borderRadius: '3px',
        marginTop: '10px',
        width: '100%!important',
        height: '40px'
    },
    contactInforFormLabel: {
        color: '#225384',
        fontWeight: 600
    },
    businessAdressBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    formControl: {
        width: '100%!important'
    },
    informationOptions: {
        marginTop: '5em',
        paddingBottom: '2em',
        display: 'flex',
        justifyContent: 'space-between'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important',
        background: '#1BABE2!important'
    },
    businessOwnerFields: {
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '1em'
        },
        [theme.breakpoints.up('md')]: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2,1fr)',
            rowGap: '1em',
            columnGap: '1em'
        }
    },
    inputOwnership: {
        '& input[type="number"]::-webkit-inner-spin-button, & input[type="number"]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            '-moz-appearance': 'none',
            appearance: 'none',
            margin: 0
        }
    }
}));

export const OwnershipDisclosureModal = ({ setOwnershipDisclosureModalOpen }) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [loadingSign, setLoadingSign] = useState(false);
    const [label, setLabel] = useState('');
    const [openAlert, setOpenAlert] = useState(false);
    const [url, setUrl] = useState('');
    const [isSignButtonClicked, setIsSignButtonClicked] = useState(false);
    const [isSignValidated, setIsSignValidated] = useState(true);
    const [isFirstTryValidation, setIsFirstTryValidation] = useState(true);
    const [countdown, setCountdown] = useState(false);
    const [counter, setCounter] = useState(20);
    const [nextDisabled, setNextDisabled] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { businessInfo } = useContext(AuthContext);

    useEffect(() => {
        let intervalId;

        if (countdown && counter > 0) {
            setNextDisabled(true);
            intervalId = setInterval(() => {
                setCounter(prevCounter => prevCounter - 1);
            }, 1000);
        } else if (counter === 0) {
            setNextDisabled(false);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [countdown, counter]);

    const generateOwnershipDisclosure = async (retryCount = 0) => {
        try {
            const response = await KYB.generateOwnershipDisclosure(businessInfo.id);
            setUrl(response.url);
            setLoadingSign(false);
        } catch (error) {
            console.error(error);
            if (retryCount < 3) {
                setTimeout(() => generateOwnershipDisclosure(retryCount + 1), 2000);
            }
        }
    };
    useEffect(() => {
        document.getElementById('kyb-taxheader')?.scrollIntoView();
        setLoadingSign(true);
        generateOwnershipDisclosure();
    }, []);

    const sendInfo = async () => {
        const ownershipDisclosureStatus = await KYB.retrieveOwnershipDisclosure(businessInfo.id);

        if (
            ownershipDisclosureStatus.status === OWNERSHIP_DISCLOSURE_STATUS.COMPLETED ||
            ownershipDisclosureStatus.signedAt !== ''
        ) {
            setLoading(true);
            setOwnershipDisclosureModalOpen(false);
            setLoading(false);
        } else if (isFirstTryValidation) {
            setIsFirstTryValidation(false);
            setCountdown(true);
        } else {
            setCountdown(false);
            setIsSignValidated(false);
            setIsSignButtonClicked(false);
            setIsFirstTryValidation(true);
            setCounter(20);
            generateOwnershipDisclosure();
        }
    };

    const SignButtonHandler = () => {
        setIsSignValidated(true);
        setIsSignButtonClicked(true);
    };

    const nextIsDisabled = () => {
        return !isSignButtonClicked || nextDisabled;
    };

    return (
        <Dialog open={true} fullScreen={fullScreen} maxWidth="lg">
            <DialogContent>
                <Box className={classes.dialogBody}>
                    <Box className={classes.content}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="h5" className={classes.contentTitle}>
                                    Ownership Disclosure Endorsement
                                </Typography>
                                <Typography variant="subtitle1" style={{ color: '#A8B6C1' }}>
                                    We will need this to comply with new banking regulations
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            style={{
                                background: '#F1F3F7',
                                borderRadius: '4px',
                                marginTop: '32px',
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'start'
                            }}
                            sx={{
                                maxWidth: {
                                    lg: 'auto',
                                    md: 'auto',
                                    sm: 'auto',
                                    xs: 'auto'
                                },
                                maxHeight: {
                                    lg: '140px',
                                    md: 'auto',
                                    sm: 'auto',
                                    xs: 'auto'
                                }
                            }}>
                            <Box>
                                <InfoIcon
                                    style={{
                                        color: 'black',
                                        marginRight: '3px',
                                        marginLeft: '10px',
                                        marginTop: '13px'
                                    }}
                                />
                            </Box>
                            <Box sx={{ padding: '8px' }}>
                                <label
                                    style={{
                                        fontFamily: 'Montserrat',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        fontSize: '13px',
                                        lineHeight: '20px',
                                        color: '#7D95A5'
                                    }}>
                                    The control person of the business should sign the business ownership document. This document
                                    contains details of the business and its owners who have 25% or above ownership. This document
                                    must be signed even if no owners have 25% or above ownership but have significant
                                    responsibility in managing the DeFi account.
                                </label>
                            </Box>
                        </Box>
                        <Box marginTop="30px" flexDirection="column">
                            <FormControl>
                                <Typography variant="subtitle2">Please sign the Ownership Disclosure Endorsement </Typography>
                                <Typography variant="subtitle1">
                                    You will be taken to a different website where you will sign the agreement. Once you have
                                    signed it, please return to this screen and click Verify
                                </Typography>
                                <Box sx={{ marginTop: '20px' }}>
                                    {loadingSign ? (
                                        <Box marginLeft="auto" sx={{ float: 'left' }}>
                                            <CircularComponent />
                                        </Box>
                                    ) : (
                                        <>
                                            {' '}
                                            {isSignButtonClicked ? (
                                                <Button
                                                    variant="outlined"
                                                    style={{ fontWeight: 'bold' }}
                                                    onClick={SignButtonHandler}>
                                                    {isSignButtonClicked
                                                        ? '✓ Ownership Disclosure Endorsement Signature Initiated'
                                                        : 'Sign Ownership Disclosure Endorsement'}
                                                </Button>
                                            ) : (
                                                <a href={url} target="_blank" rel="noopener noreferrer">
                                                    <Button
                                                        variant="outlined"
                                                        style={{ fontWeight: 'bold' }}
                                                        onClick={SignButtonHandler}>
                                                        {isSignButtonClicked
                                                            ? '✓ Ownership Disclosure Endorsement Signature Initiated'
                                                            : 'Sign Ownership Disclosure Endorsement'}
                                                    </Button>
                                                </a>
                                            )}
                                            {isSignButtonClicked && isSignValidated && (
                                                <Typography variant="subtitle1" sx={{ fontSize: '11px' }}>
                                                    Click next to verify the signature
                                                </Typography>
                                            )}
                                            {!isSignValidated && !isSignButtonClicked && (
                                                <Typography variant="subtitle1" sx={{ fontSize: '11px', color: '#FF002B' }}>
                                                    We couldn’t verify the signature. Try again
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </FormControl>
                        </Box>
                        <Box className={classes.informationOptions}>
                            {loading ? (
                                <Box marginLeft="auto" sx={{ float: 'right' }}>
                                    <CircularComponent />
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        {countdown ? (
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontSize: '11px', textAlign: 'right', width: '60%' }}>
                                                We were not able to confirm that you have successfully signed the document. If you
                                                already did, please retry{' '}
                                                <b>
                                                    {counter > 0
                                                        ? `in ${counter} ${counter > 1 ? 'seconds' : 'second'}.`
                                                        : 'now.'}
                                                </b>
                                            </Typography>
                                        ) : (
                                            <Box sx={{ fontSize: '11px', textAlign: 'right', width: '635px' }} />
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                            <Button variant="contained" disabled={nextIsDisabled()} onClick={() => sendInfo()}>
                                                Verify
                                            </Button>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                    <StatusSnackbar
                        label={label}
                        severity={'error'}
                        open={openAlert}
                        setOpenAlert={setOpenAlert}
                        needsAction={false}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};
